import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { collection, doc, getDoc, onSnapshot } from "firebase/firestore";
import { db } from "../../../backend/firebaseConfig";

const AdviserListWithUnappointedContext = createContext({
  advisers: [],
  loadingAdvisers: true,
});

function buildAdviserDisplayName(data = {}, docId = "") {
  const firstName = data?.firstName || data?.forename || "";
  const preferredName = data?.preferredName || "";
  const lastName = data?.lastName || data?.surname || "";

  const personName = [preferredName || firstName, lastName]
    .filter(Boolean)
    .join(" ")
    .trim();

  return (
    personName ||
    data?.tradingName ||
    data?.tradingStyle ||
    data?.brandName ||
    data?.email ||
    docId
  );
}

export const AdviserListWithUnappointedProvider = ({ children }) => {
  const [advisers, setAdvisers] = useState([]);
  const [loadingAdvisers, setLoadingAdvisers] = useState(true);

  useEffect(() => {
    const ref = collection(db, "Authorized Members", "Advisers", "Data");
    let cancelled = false;

    const unsub = onSnapshot(
      ref,
      async (snap) => {
        const next = await Promise.all(
          snap.docs.map(async (docSnap) => {
            const data = docSnap.data() || {};
            const email = data?.email || docSnap.id || "";
            const isUnappointed = data?.isAppointed === false;
            let personal = {};
            try {
              const personalSnapshot = await getDoc(
                doc(
                  db,
                  "Authorized Members",
                  "Advisers",
                  "Data",
                  docSnap.id,
                  "Personal Details",
                  "Data",
                ),
              );
              personal = personalSnapshot.exists()
                ? personalSnapshot.data()
                : {};
            } catch {
              personal = {};
            }
            const combined = { ...data, ...personal };

            return {
              id: email,
              value: email,
              email,
              adviserId: docSnap.id,
              name: buildAdviserDisplayName(combined, docSnap.id),
              firstName:
                personal?.forename ||
                personal?.firstName ||
                data?.firstName ||
                data?.forename ||
                "",
              lastName:
                personal?.surname ||
                personal?.lastName ||
                data?.lastName ||
                data?.surname ||
                "",
              preferredName:
                personal?.preferredName || data?.preferredName || "",
              dob:
                personal?.dob ||
                personal?.dateOfBirth ||
                personal?.birthDate ||
                data?.dob ||
                data?.dateOfBirth ||
                data?.birthDate ||
                null,
              tradingName: data?.tradingName || "",
              tradingStyle: data?.tradingStyle || "",
              brandName:
                data?.brandName ||
                data?.tradingName ||
                data?.tradingStyle ||
                "",
              isAR: !!data?.isAR,
              isAppointed: !isUnappointed,
              isUnappointed,
            };
          }),
        );

        if (!cancelled) {
          setAdvisers(next.filter(Boolean));
          setLoadingAdvisers(false);
        }
      },
      (error) => {
        console.error("Failed to load adviser list:", error);
        setAdvisers([]);
        setLoadingAdvisers(false);
      },
    );

    return () => {
      cancelled = true;
      unsub();
    };
  }, []);

  const value = useMemo(
    () => ({ advisers, loadingAdvisers }),
    [advisers, loadingAdvisers],
  );

  return (
    <AdviserListWithUnappointedContext.Provider value={value}>
      {children}
    </AdviserListWithUnappointedContext.Provider>
  );
};

export const useAdviserListWithUnappointed = () =>
  useContext(AdviserListWithUnappointedContext);
